/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/* Direct header predictive product search */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.header__search-bar--direct').forEach(function (root) {
    var form = root.querySelector('form[role="search"]');
    var input = root.querySelector('input[name="q"]');
    if (!form || !input || root.querySelector('.direct-search-results')) return;

    var panel = document.createElement('div');
    panel.className = 'direct-search-results';
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;
    root.appendChild(panel);

    var debounceTimer;
    var activeRequest;

    function closePanel() {
      panel.hidden = true;
      panel.replaceChildren();
    }

    function addMessage(message) {
      var status = document.createElement('p');
      status.className = 'direct-search-results__status';
      status.textContent = message;
      panel.replaceChildren(status);
      panel.hidden = false;
    }

    function renderProducts(products, query) {
      var fragment = document.createDocumentFragment();

      products.forEach(function (product) {
        var item = document.createElement('a');
        item.className = 'direct-search-result';
        item.href = product.url;
        item.setAttribute('role', 'option');

        var imageData = product.featured_image;
        var imageUrl = typeof imageData === 'string' ? imageData : imageData && imageData.url;
        if (imageUrl) {
          var image = document.createElement('img');
          image.className = 'direct-search-result__image';
          image.src = imageUrl;
          image.alt = (imageData && imageData.alt) || product.title || '';
          image.loading = 'lazy';
          item.appendChild(image);
        } else {
          var placeholder = document.createElement('span');
          placeholder.className = 'direct-search-result__image direct-search-result__image--empty';
          item.appendChild(placeholder);
        }

        var details = document.createElement('span');
        details.className = 'direct-search-result__details';

        var title = document.createElement('span');
        title.className = 'direct-search-result__title';
        title.textContent = product.title || '';
        details.appendChild(title);

        if (product.price) {
          var price = document.createElement('span');
          price.className = 'direct-search-result__price';
          price.textContent = product.price;
          details.appendChild(price);
        }

        item.appendChild(details);
        fragment.appendChild(item);
      });

      var allResults = document.createElement('a');
      allResults.className = 'direct-search-results__all';
      allResults.href = form.action + '?type=product&q=' + encodeURIComponent(query);
      allResults.textContent = 'View all results';
      fragment.appendChild(allResults);

      panel.replaceChildren(fragment);
      panel.hidden = false;
    }

    function requestSuggestions() {
      var query = input.value.trim();
      if (query.length < 2) {
        closePanel();
        return;
      }

      if (activeRequest) activeRequest.abort();
      activeRequest = new AbortController();

      var rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      var url = rootPath + 'search/suggest.json?resources[type]=product&resources[limit]=6&q=' + encodeURIComponent(query);

      fetch(url, {
        signal: activeRequest.signal,
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Search request failed');
          return response.json();
        })
        .then(function (data) {
          var products = data && data.resources && data.resources.results
            ? data.resources.results.products || []
            : [];

          if (input.value.trim() !== query) return;
          if (products.length) renderProducts(products, query);
          else addMessage('No matching products found');
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') closePanel();
        });
    }

    input.addEventListener('input', function () {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(requestSuggestions, 250);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2) requestSuggestions();
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closePanel();
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) closePanel();
    });
  });
});
